
import { type Component, inject, type Ref, ref, shallowRef } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { socketIO } from '@/lib/socket/socket.ts'
import { useEditorStore } from '@/stores/editor.ts'
import type { UploadFile } from '@/types/uploadFile'
import { axiosRequest } from '@/lib/axios/axios.ts'
import type { Lang, UserInfo, TabList, TabItem, ModalButton, Participant, Department, ProjectInfo, SchemaItem, DropboxItem, TemplateItem, ProjectItem, ListItem } from '@/types/common'

export const useCommonStore = defineStore('common', () => {
	const langInject: Ref<any> = ref(inject('lang'))
	const listType = ref<string>('')
	const baseUrl: Ref<string, string> = ref(sessionStorage.getItem('baseUrl') || '/api')
	const dashboardListType = ref<string>(localStorage.getItem('DLT') || 'K')
	const socket: Ref<any> = ref(null)
	const socketTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
	const activeTab = ref<string>('Dashboard')
	const tabList = ref<TabList>({})
	const projectParticipant = ref<Participant[]>([])
	const projectDepartment = ref<Department[]>([])
	const expandProjectUid = ref('')
	const component = shallowRef<Component | null>(null)
	const { noteUid } = storeToRefs(useEditorStore())
	type noteInfoType = {
		projectTitle?: string
		title: string
		uid: string
		projectUid: string
		description: string
	}
	const projectInfo = ref<ProjectInfo>({})
	const noteInfo = ref<noteInfoType>({
		title: '',
		uid: '',
		projectUid: '',
		description: '',
	})
	const subActiveTab = ref('')
	const currentProjectUid = ref('')
	// 사이드 축소/확장 토글 상태
	const isAsideCollapsed = ref(false)

	const asideBoardList = ref<any[]>([])
	const userName = ref('')

	const isApiLoading = ref(false)
	const isFileUploading = ref(false)
	const progressPercentage = ref(0)
	const uploadFileList = ref<UploadFile[]>([])
	const schemaList = ref<SchemaItem[]>([])
	const dropboxList = ref<DropboxItem[]>([])
	const projectList = ref<ProjectItem[]>([])
	const templateList = ref<TemplateItem[]>([])
	// [HOTFIX] auth를 localStorage에서 복원 — 새로고침 시 beforeEach에서 admin 체크 실패하여 Dashboard로 튕기는 문제 수정
	// 문제 발생 시 이 블록의 auth 라인을 `auth: {}` 로 원복하고, Info.vue·router/index.ts의 같은 주석 블록도 함께 원복할 것
	const savedAuth = (() => { try { return JSON.parse(localStorage.getItem('userAuth') || '{}') } catch { return {} } })()
	const userInfo = ref<UserInfo>({
		info: {
			userId: localStorage.getItem('userId') || '',
			userName: localStorage.getItem('userName') || '',
		},
		auth: savedAuth,
	})
	const originAutoLogout = ref(60)
	const autoLogout = ref(60)
	const autoLogoutInterval = ref<ReturnType<typeof setInterval> | null>(null)
	const autoLogoutDeadline = ref<number | null>(null)

	const alarmCount = ref<number>(0)

	// 알람 실시간 count 반영하기 위한 함수
	const setAlarmCount = (count: number) => {
		alarmCount.value = Number(count) || 0
	}

	const refreshAlarmCount = async () => {
		try {
			const res = await axiosRequest('/v1/dashboard/review/mentionCount', {}, {}) as { cnt?: number }
			alarmCount.value = res?.cnt ? Number(res.cnt) : 0
		} catch (e) {
			alarmCount.value = 0
		}
	}
	const langReturnFunction = () => {
		let langParams: string = localStorage.getItem('lang') || 'en'
		if (langParams === 'ko') langParams = 'kr'

		if (langInject.value && langInject.value[langParams]) {
			return langInject.value[langParams]
		}
		// Fallback for tests or when injection is missing
		return {
			button: { checkin: 'Check In', delete: 'Delete', save: 'Save', ok: 'OK', cancel: 'Cancel', renew: 'Renew' },
			modal: {
				error: { title: 'Error' },
				title: 'Title',
				save: 'Save',
				noteSave: 'Note Save',
				templateList: { delete: 'Delete' },
				template: { deleteAlarm: 'Delete Alarm', version: 'Version' },
			},
			project: { note: { sideMenu: { auditTrail: 'Audit', signNote: 'Sign', noteSetting: 'Setting', reopenProject: 'Reopen', noteCreate: 'Create Note', endProject: 'End' } } },
			template: { sideMenu: { setting: 'Setting', approve: 'Approve' } },
			menu: { template: 'Template' },
		}
	}

	const setLang = (type: string) => {
		localStorage.setItem('lang', type)
		lang.value = langInject.value[type]
	}

	const lang = ref<Lang>(langReturnFunction())

	// 250707 유저 정보 설정
	const setUserInfo = (info: UserInfo['info'], auth: UserInfo['auth']) => {
		userInfo.value.info = info
		userInfo.value.auth = auth
	}

	//공통 list type
	const list = ref<ListItem[]>([])

	// 공통 tab store
	const tab = ref<string>('')

	const uniqueId = () => {
		const newDate = new Date().getTime()
		const characters: string = 'abcdefghijklmnopqrstuvwxyz'
		let randomCharacterOne = characters.charAt(Math.ceil(Math.random() * characters.length))
		return randomCharacterOne + Math.random().toString(36).substring(2, 16) + '' + (newDate + Math.ceil(Math.random() * 100000000))
	}

	const modals = ref<{ [key: string]: boolean }>({
		create: false,
		edit: false,
		view: false,
		default: false,
		confirm: false,
		asideCreate: false,
		error: false,
	})

	// 250707 공통 alert confirm modal
	const show = ref(false)
	const modalTitle = ref('')
	const modalMessage = ref('')
	const modalFunction = ref<(() => void) | null>(null)
	const modalType = ref('default')
	const modalBtnType = ref<ModalButton[]>([])

	const btnType: Record<string, ModalButton[]> = {
		error: [{ type: ['btn--primary'], text: lang.value.button.ok }],
		default: [{ type: ['btn--primary'], text: lang.value.button.ok }],
		info: [{ type: ['btn--primary'], text: lang.value.button.ok }],
		confirm: [
			{ type: ['btn--default'], text: lang.value.button.cancel },
			{ type: ['btn--primary'], text: lang.value.button.ok },
		],
		// 취소/확인 + 알림(info) 아이콘 (프로젝트 종료 확인 등)
		confirmInfo: [
			{ type: ['btn--default'], text: lang.value.button.cancel },
			{ type: ['btn--primary'], text: lang.value.button.ok },
		],
		// "취소/승인" 2버튼 confirm (템플릿 승인 등)
		confirmApprove: [
			{ type: ['btn--default'], text: lang.value.button.cancel },
			{ type: ['btn--primary'], text: lang.value.button.approval },
		],
		confirmLogout: [
			{ type: ['btn--default'], text: lang.value.button.ok },
			{ type: ['btn--primary'], text: lang.value.button.renew },
		],
	}

	// 250707 공통 alert confirm modal
	const open = (type: string = 'default', title: string = '', message: string = '', callBack: any) => {
		show.value = true
		// 아이콘: confirmLogout/confirmApprove → confirm, confirmInfo → info, 그 외 type 그대로
		if (type === 'confirmLogout' || type === 'confirmApprove') {
			modalType.value = 'confirm'
		} else if (type === 'confirmInfo') {
			modalType.value = 'info'
		} else {
			modalType.value = type
		}
		modalTitle.value = title
		modalMessage.value = message
		modalFunction.value = callBack
		modalBtnType.value = btnType[type]
	}
	const close = () => {
		show.value = false
		modalType.value = 'default'
		modalTitle.value = ''
		modalMessage.value = ''
		modalFunction.value = null
		modalBtnType.value = []
	}

	const confirm = () => {
		if (modalFunction.value) modalFunction.value()
		close()
	}

	const setListType = (type: string) => {
		dashboardListType.value = type
		localStorage.setItem('DLT', type)
	}

	const socketConnect = () => {
		socket.value = socketIO
		socket.value.connect()
	}

	const TAB_ACTIVE_KEY = '_active'
	const ensureTabActive = () => {
		if (!tabList.value[TAB_ACTIVE_KEY] || typeof tabList.value[TAB_ACTIVE_KEY] !== 'object') {
			tabList.value[TAB_ACTIVE_KEY] = {}
		}
	}

	// 탭/활성 uid를 유저별로도 저장해서, 계정 전환 시 탭이 섞이지 않게 한다.
	const getCurrentUserIdForTabStorage = (): string => String(userInfo.value?.info?.userId || localStorage.getItem('userId') || '').trim()
	const persistTabListToStorage = (activeUid?: string) => {
		const tabListString = JSON.stringify(tabList.value)
		// 호환(기존): 전역 키도 유지
		localStorage.setItem('tabList', tabListString)

		const uid = getCurrentUserIdForTabStorage()
		if (uid) {
			localStorage.setItem(`tabList:${uid}`, tabListString)
			if (typeof activeUid === 'string') {
				localStorage.setItem(`noteUid:${uid}`, activeUid)
			}
		}
	}

	const setTabList = (name: string, note: TabItem, projectUid?: string, projectTitle?: string, description?: string, mentionUid?: string) => {
		if (tabList.value[name] === undefined) {
			tabList.value[name] = []
		}
		// note : 해당 탭의 내용
		if (projectUid) {
			note = {
				...note,
				projectUid,
				projectTitle,
				mentionUid,
				description,
			}
			currentProjectUid.value = projectUid
		}

		// 기존 항목이 있는지 확인
		const tabItems = tabList.value[name] as TabItem[]
		const existingIndex = tabItems.findIndex((item: TabItem) => {
			return item.uid === note.uid
		})

		if (existingIndex !== -1) {
			// 기존 항목이 있으면 업데이트
			tabItems[existingIndex] = { ...tabItems[existingIndex], ...note }
			tabItems[existingIndex].mentionUid = mentionUid
		} else {
			// 새 항목이면 추가
			tabItems.push(note)
		}

		// 현재 활성 탭 uid 저장 (tabList 내부 메타)
		ensureTabActive()
		const activeTab = tabList.value[TAB_ACTIVE_KEY] as { [key: string]: string }
		activeTab[name] = note.uid
		persistTabListToStorage(String(note.uid || ''))

		noteUid.value = note.uid
		localStorage.setItem('noteUid', note.uid)
	}

	const deleteTabList = (name: string, uid: string) => {
		const tabItems = tabList.value[name] as TabItem[] | undefined
		if (!Array.isArray(tabItems)) return
		tabList.value[name] = tabItems.filter((item: TabItem) => item.uid !== uid)

		// 활성 탭이 닫힌 경우: 남은 탭의 첫 uid로 활성값 갱신 (없으면 빈값)
		ensureTabActive()
		const activeTab = tabList.value[TAB_ACTIVE_KEY] as { [key: string]: string }
		if (activeTab?.[name] === uid) {
			const remainingItems = tabList.value[name] as TabItem[]
			activeTab[name] = remainingItems?.[0]?.uid || ''
		}
		persistTabListToStorage(String(activeTab?.[name] || ''))
	}
	const changeTabList = (name: string, oldUid: string, note: TabItem, projectUid?: string) => {
		const tabItems = tabList.value[name] as TabItem[]
		tabItems.forEach((item: TabItem) => {
			if (item.uid === oldUid) {
				item.uid = note.uid
				item.title = note.title
				item.description = note.description
				item.projectUid = projectUid
			}
		})

		// uid 변경 시 활성 탭 메타도 함께 갱신
		ensureTabActive()
		const activeTab = tabList.value[TAB_ACTIVE_KEY] as { [key: string]: string }
		if (activeTab?.[name] === oldUid) {
			activeTab[name] = note.uid
		}
		persistTabListToStorage(String(note.uid || ''))

		noteUid.value = note.uid
		localStorage.setItem('noteUid', note.uid)
	}
	const setProjectList = (list: ProjectItem[]) => {
		projectList.value = list
	}
	const setTemplateList = (list: TemplateItem[]) => {
		templateList.value = list
	}
	const setSchemaList = (list: SchemaItem[]) => {
		schemaList.value = list
	}
	const setDropboxList = (list: DropboxItem[]) => {
		dropboxList.value = list
	}
	const startLoading = () => {
		isApiLoading.value = true
	}

	const endLoading = () => {
		if (!isApiLoading.value) return

		// 일부러 0.2초 이후 종료되게 해달라는 요청사항
		setTimeout(() => {
			isApiLoading.value = false
		}, 200)
	}

	const startFileUploading = () => {
		isFileUploading.value = true
	}

	const setFileData = (fileData: UploadFile) => {
		if (uploadFileList.value.length > 2) {
			uploadFileList.value.shift()
		}

		uploadFileList.value.push(fileData)
	}

	const getAutoLogout = () => {
		return originAutoLogout.value
	}

	let isLoggingOut = false

	const performAutoLogout = () => {
		if (isLoggingOut) return
		isLoggingOut = true


		if (socket.value) {
			try {
				socket.value.leaveParticipant()
				socket.value.socket?.disconnect()
			} catch (error) {
			}
		}

		if (autoLogoutInterval.value) {
			clearInterval(autoLogoutInterval.value)
			autoLogoutInterval.value = null
		}

		localStorage.removeItem('accessToken')
		localStorage.removeItem('refreshToken')
		localStorage.removeItem('userId')
		localStorage.removeItem('userName')

		if ((window as any).removeUserActivityListeners) {
			;(window as any).removeUserActivityListeners()
		}

		window.location.href = '/sign-in'
	}

	const setAutoLogout = (time: number) => {
		const normalizedTime = Number(time) || 0
		originAutoLogout.value = normalizedTime
		autoLogoutDeadline.value = Date.now() + normalizedTime * 60 * 1000

		const updateRemaining = () => {
			if (!autoLogoutDeadline.value) return

			const remainingMs = autoLogoutDeadline.value - Date.now()
			if (remainingMs <= 0) {
				autoLogout.value = 0
				performAutoLogout()
				return
			}

			const remainingMinutes = Math.ceil(remainingMs / (60 * 1000))
			if (autoLogout.value !== remainingMinutes) {
				autoLogout.value = remainingMinutes
			}
		}

		if (autoLogoutInterval.value) clearInterval(autoLogoutInterval.value)
		autoLogoutInterval.value = setInterval(updateRemaining, 1000)
		updateRemaining()

		return autoLogout.value
	}

	return {
		component,
		baseUrl,
		listType,
		lang,
		userInfo,
		userName,
		isApiLoading,
		startLoading,
		endLoading,
		isFileUploading,
		startFileUploading,
		progressPercentage,
		uploadFileList,
		setFileData,
		noteInfo,
		currentProjectUid,
		list,
		tab,
		modals,
		dashboardListType,
		subActiveTab,
		setListType,
		setLang,
		setUserInfo,
		open,
		close,
		confirm,
		uniqueId,
		socketConnect,
		socket,
		socketTimeout,
		activeTab,
		tabList,
		setTabList,
		deleteTabList,
		changeTabList,
		projectParticipant,
		projectDepartment,
		asideBoardList,
		show,
		modalTitle,
		modalMessage,
		modalFunction,
		modalType,
		modalBtnType,
		schemaList,
		dropboxList,
		setSchemaList,
		setDropboxList,
		setProjectList,
		setTemplateList,
		projectList,
		templateList,
		expandProjectUid,
		// aside collapse
		isAsideCollapsed,
		alarmCount,
		setAlarmCount,
		refreshAlarmCount,
		autoLogout,
		setAutoLogout,
		getAutoLogout,
		autoLogoutInterval,
		autoLogoutDeadline,
		projectInfo,
	}
})
